import React, {useEffect, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Button,
    IconButton,
    ListItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import {StackProps} from "@mui/material/Stack/Stack";
import {PurgatoryItemModel} from "./model";
import {rejectPurgatoryItem, approvePurgatoryItem, findBySeriesTitle, deletePage} from "../api";
import './purgatory.css'
import {ApproveRequest} from "../comics/model";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Grid2 from "@mui/material/Grid2";

interface PurgatoryItemProps {
    item: PurgatoryItemModel,
    handleDecision: (id: Number) => void
}

export default function PurgatoryItem(props: PurgatoryItemProps) {

    const [viewModelState, setViewModelState] = useState({seriesId: null, title: props.item.meta.seriesName})
    const id = props.item.id
    const [number, setNumber] = useState(props.item.meta.number)
    const [summary, setSummary] = useState(props.item.meta.summary)
    const [publicationDate, setPublicationDate] = useState(new Date())
    const [pagesCount, setPagesCount] = useState(props.item.meta.pagesCount)
    const [publisher, setPublisher] = useState(props.item.meta.publisher)

    const [seriesOptions, setSeriesOptions] = useState<any[]>([])

    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    };

    useEffect(() => {
        setOptions()
    }, []);

    function onReject() {
        rejectPurgatoryItem(id, props.handleDecision)
    }

    function onApprove() {
        const request: ApproveRequest = {
            id: id,
            seriesUpdate: {
                id: viewModelState.seriesId,
                title: viewModelState.title,
                publisher: publisher
            },
            issueUpdate: {
                number: number,
                summary: summary,
                publicationDate: publicationDate,
                pagesCount: pagesCount
            }
        }

        approvePurgatoryItem(request, props.handleDecision)
    }

    function setOptions() {
        findBySeriesTitle(viewModelState.title, (data) => {
            setSeriesOptions(data)
        })
    }

    const range = (start: number, end: number): number[] => {
        return Array.from(Array(end - start).keys()).map((i) => i + start);
    };

    return <Grid2 container spacing={0} width={650}>
        {props.item.meta.pagesCount != 0 ?
            (
                <Stack direction="column" spacing={1}>
                    <ListItem key={pagesCount}>
                        <img
                            src={`${process.env.REACT_APP_SERVER_URL}/private/comics/purgatory/file/${props.item.id}/0`}
                            alt="cover"
                            className='Сover'
                        />
                    </ListItem>
                    <ListItem>
                        <Actions
                            direction="row"
                            spacing={1}
                            onReject={onReject}
                            onApprove={onApprove}
                        />
                    </ListItem>
                </Stack>
            ) : null
        }

        <Stack direction="column" spacing={1} paddingTop={0}>
            <ListItem key="Title">
                <Autocomplete
                    value={viewModelState}
                    freeSolo={true}
                    options={seriesOptions}
                    getOptionLabel={(option) => option.title}
                    onChange={(_: any, newValue: any | null) => {
                        if (newValue === null) {
                            setViewModelState(state => Object.assign(state, {seriesId: null, title: null}))
                        } else {
                            setViewModelState(state => Object.assign(state, {
                                seriesId: newValue.id,
                                title: newValue.title
                            }))
                        }
                    }}
                    onInputChange={(_, newInputValue) => {
                        setViewModelState(state => Object.assign(state, {title: newInputValue}))
                        setOptions()
                    }}
                    defaultValue={viewModelState}
                    sx={{width: 300}}
                    renderInput={(params) => <TextField {...params}
                                                        label={`Title ${viewModelState.seriesId === null ? 'unlinked' : 'linked'}`}
                                                        variant="outlined"/>}
                />
            </ListItem>
            <ListItem key="Title suggestions">
                <Accordion sx={{width: 300}} hidden={seriesOptions.length === 0}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon/>}
                        aria-controls="panel2-content"
                        id="panel2-header"
                    >
                        <Typography component="span">Suggestions</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {seriesOptions.map(opt =>
                            <Tooltip
                                title={`${new Date(opt.firstPublication).toLocaleDateString(undefined, options)} - ${new Date(opt.lastPublication).toLocaleDateString(undefined, options)}`}>
                                <div>

                                    {`[${opt.id}] ${opt.title} ${opt.ended ? '✔' : ''}`}

                                    <Button onClick={() =>
                                        setViewModelState({...viewModelState, seriesId: opt.id, title: opt.title})
                                    }
                                    >Link</Button>
                                </div>
                            </Tooltip>
                        )}
                    </AccordionDetails>
                </Accordion>
            </ListItem>
            <ListItem key="Number">
                <TextField id="Number" label="Number" variant="outlined" value={number}
                           onChange={v => setNumber(v.currentTarget.value)}/>
            </ListItem>
            <ListItem key="Publisher">
                <TextField id="Publisher" label="Publisher" variant="outlined" value={publisher}
                           onChange={v => setPublisher(v.currentTarget.value)}/>
            </ListItem>
            <ListItem key="Summary">
                <TextField id="Summary" label="Summary" variant="outlined" multiline={true} sx={{width: 300}}
                           value={summary}
                           onChange={v => setSummary(v.currentTarget.value)}/>
            </ListItem>
            <ListItem key="Pages count">
                <TextField id="Pages count" label="Pages count" variant="outlined" value={pagesCount}
                           onChange={v => setPagesCount(Number.parseInt(v.currentTarget.value))}/>
            </ListItem>
            <ListItem key="Publication date">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Publication date"
                        value={dayjs(publicationDate)}
                        onChange={value => setPublicationDate(value?.toDate() ?? new Date())}
                    />
                </LocalizationProvider>
            </ListItem>
            {props.item.meta.pagesCount == 0 ? (
                <ListItem key="Actions">
                    <Actions
                        direction="row"
                        spacing={1}
                        onReject={onReject}
                        onApprove={onApprove}
                    />
                </ListItem>
                ) : null
            }

        </Stack>
        <Accordion className={'Pages-List'}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                aria-controls="panel2-content"
                id="panel2-header"
            >
                <Typography component="span">Pages</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid2 direction={"row"} key={pagesCount}>
                    {range(0, pagesCount).map(item =>
                        <IconButton
                            aria-label="delete"
                            onClick={() => {
                                deletePage(id, item, () => {
                                    setPagesCount((prevState) => prevState - 1)
                                })
                            }}
                        >
                            <img
                                src={`${process.env.REACT_APP_SERVER_URL}/private/comics/purgatory/file/${id}/${item}?size=SMALL`}
                                alt="cover"
                                width={150}
                                key={item}
                            />
                            <DeleteIcon/>
                        </IconButton>
                    )}
                </Grid2>
            </AccordionDetails>
        </Accordion>

    </Grid2>
}

interface ActionsProps extends StackProps {
    onApprove?: () => void
    onReject?: () => void
}

const Actions: React.FC<ActionsProps> = ({...stackProps}) => {
    return (
        <Stack {...stackProps}>
            <Button variant="contained" color="success" onClick={stackProps.onApprove}>
                Approve
            </Button>
            <Button variant="contained" color="error" onClick={stackProps.onReject}>
                Reject
            </Button>
        </Stack>
    );
};
